// components/podcast/PodcastEpisodeList.tsx
import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";
import {
  fetchPodcastEpisodes,
  deletePodcastEpisode,
  PodcastEpisode,
  PaginatedResponse,
} from "../../services/api";
import { useSnackbar } from "notistack";

const PodcastEpisodeList: React.FC = () => {
  const { podcastId } = useParams<{ podcastId: string }>();
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, error } = useQuery<
    PaginatedResponse<PodcastEpisode>
  >({
    queryKey: ["podcastEpisodes", podcastId, page + 1, rowsPerPage],
    queryFn: () =>
      fetchPodcastEpisodes(page + 1, rowsPerPage, Number(podcastId)),
    enabled: !!podcastId,
  });

  const deleteMutation = useMutation({
    mutationFn: deletePodcastEpisode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["podcastEpisodes", podcastId],
      });
      enqueueSnackbar("Episode deleted successfully", { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar("Failed to delete episode", { variant: "error" });
    },
  });

  const handleDeleteClick = (id: number) => setDeleteId(id);
  const handleDeleteConfirm = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };
  const handleDeleteCancel = () => setDeleteId(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">
          Error loading episodes. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Episodes</Typography>
        <Button
          component={Link}
          to={`/podcasts/${podcastId}/episodes/new`}
          variant="contained"
          color="primary"
          startIcon={<PlayIcon />}
        >
          Add New Episode
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="80">Episode #</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell width="100">Duration</TableCell>
              <TableCell width="100">Status</TableCell>
              <TableCell width="200">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.items.map((episode) => (
              <TableRow key={episode.id}>
                <TableCell>{episode.episodeNumber}</TableCell>
                <TableCell>{episode.title}</TableCell>
                <TableCell>{episode.shortDescription}</TableCell>
                <TableCell>{episode.audioDuration || "N/A"}</TableCell>
                <TableCell>
                  <Chip
                    label={episode.active ? "Active" : "Inactive"}
                    color={episode.active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {episode.audioUrl && (
                      <Tooltip title="Play Audio">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            window.open(episode.audioUrl, "_blank")
                          }
                        >
                          <PlayIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Edit Episode">
                      <IconButton
                        component={Link}
                        to={`/podcasts/${podcastId}/episodes/edit/${episode.id}`}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Episode">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(episode.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!data?.items.length && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" sx={{ py: 2 }}>
                    No episodes found. Add your first episode!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data && data.items.length > 0 && (
        <TablePagination
          component="div"
          count={data.totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 2 }}
        />
      )}

      <Dialog open={deleteId !== null} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this episode? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PodcastEpisodeList;
